import { useEffect, useRef, useState } from "react";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import { toast } from "react-toastify";
import { getMapView, LyrxFileWithOptions, uploadDepthData } from "../../utils/arcgisUtils";

import TrashIcon from "../../assets/trash-can.svg?react";
import "./DataImport.css";
import RadioButtonGroup from "../genericComponents/RadioButton/RadioButtonGroup";
import InputField from "../genericComponents/InputField/InputField";
import { ImageryLayerNFS } from "../../extensions/FeatureLayerNFS";

const DepthDataImport = () => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [cellSize, setCellSize] = useState<number>(100); // Default cell size in centimeters
    const [wkid, setWkid] = useState<number>(25832); // Default spatial reference WKID
    const [decimalSeparator, setDecimalSeparator] = useState<"Punkt" | "Komma">("Punkt");

    const [isUploading, setIsUploading] = useState<boolean>(false);

    const validFileExtensions = [".txt", ".xyz", ".TXT", ".XYZ"];

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // Drag and drop area state and handlers
    const dropZoneRef = useRef<HTMLDivElement | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            // Filter out unsupported file types
            const invalidFileNames = Array.from(files)
                .filter((file) => !validFileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)))
                .map((file) => file.name);

            const filteredFiles = Array.from(files).filter((file) =>
                validFileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
            );
            if (invalidFileNames.length > 0) {
                toast.error(
                    `Følgende filer har et format som ikke støttes og ble ikke lagt til: ${invalidFileNames.join(", ")}`
                );
            }

            // Only add new files to the state
            setSelectedFiles((prev) => [...prev, ...filteredFiles]);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            // Filter out unsupported file types
            const filteredFiles = Array.from(files).filter((file) =>
                validFileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
            );
            const invalidFileNames = Array.from(files)
                .filter((file) => !validFileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)))
                .map((file) => file.name);
            if (invalidFileNames.length > 0) {
                toast.error(
                    `Følgende filer har et format som ikke støttes og ble ikke lagt til: ${invalidFileNames.join(", ")}`
                );
            }
            // Get new files that aren't already in the selected files list
            const newFiles = filteredFiles.filter(
                (newFile) => !selectedFiles.some((existingFile) => existingFile.name === newFile.name)
            );

            // Append the new files to the existing ones
            setSelectedFiles((prev) => [...prev, ...newFiles]);
        }
    };

    const importFiles = async () => {
        if (selectedFiles.length === 0) {
            toast.error("Ingen filer valgt");
            return;
        }

        setIsUploading(true);
        const filesWithOptions: LyrxFileWithOptions[] = [];

        for (let file of selectedFiles) {
            // eslint-disable-next-line no-useless-escape
            let cleanFileName = file.name
                .replaceAll("æ", "ae")
                .replaceAll("ø", "oe")
                .replaceAll("å", "aa")
                .replaceAll("Æ", "Ae")
                .replaceAll("Ø", "Oe")
                .replaceAll("Å", "Aa");
            cleanFileName = cleanFileName.replaceAll(" ", "_");
            cleanFileName = cleanFileName.replaceAll("-", "_");
            cleanFileName = cleanFileName.replaceAll(/[^a-zA-Z0-9.\\-]/g, "_");
            filesWithOptions.push({
                file: file,
                name: cleanFileName,
                spatialReferenceWkid: { latestWkid: wkid, wkid: wkid },
                cellSize: cellSize,
                decimalSeparator: decimalSeparator,
            });
        }
        try {
            const result = await uploadDepthData(filesWithOptions);
            toast.info(`Importert ${result.successCount} av ${result.totalCount} filer.`);
            if (result.successCount !== 0) {
                toast.info("Det kan ta noen minutter før dataene er synlige i dybdedatatjenesten.");
            }
        } catch (error) {
            toast.error("En feil oppsto under importen: " + error);
        }
        setSelectedFiles([]);
        setCellSize(100);
        setWkid(25832);
        setDecimalSeparator("Punkt");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setIsUploading(false);
        const mapview = getMapView();
        if (!mapview?.map) return;

        const hiResKYVDepthLayer = mapview.map.findLayerById("KYV Dybdedata Raster");
        if (hiResKYVDepthLayer) {
            (hiResKYVDepthLayer as ImageryLayerNFS).refresh();
        }
    };

    const removeFile = (fileName: string) => {
        setSelectedFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
    };

    useEffect(() => {
        return () => {
            setSelectedFiles([]);
            setCellSize(100);
            setWkid(25832);
            setDecimalSeparator("Punkt");
            setIsUploading(false);
        };
    }, []);

    return (
        <div className="efs-manager-content-container small-gap">
            <span>Støttet format: .txt, .xyz</span>
            <span>Prosesseringen kan ta litt tid, avhengig av filstørrelse og kompleksitet.</span>
            <span>Jo flere filer du prosesser samtidig, jo lengre tid kan det ta.</span>
            <label
                style={{ cursor: "pointer" }}
                role="button"
                className="nfs-button no-flex outline"
                htmlFor="file-import"
            >
                Velg filer
                <input
                    hidden
                    id="file-import"
                    name="file-import"
                    ref={fileInputRef}
                    accept=".txt,.xyz,.TXT,.XYZ"
                    placeholder="Velg filer"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                />
            </label>

            {/* Drag and drop area */}
            <div
                ref={dropZoneRef}
                className={`dataimport-dropzone${isDragActive ? " active" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {isDragActive ? "Slipp filene her..." : "Eller dra og slipp filer her"}
            </div>
            <span>
                <strong>Påkrevd info om dybdedatafil:</strong>
            </span>
            <span>Samme innstilling vil gjelde for alle valgte filer.</span>
            <InputField
                labelClassName="no-flex"
                inputFieldId="spatialReferenceWkid"
                label="Romreferanse WKID"
                type="number"
                value={wkid}
                onChange={(value) => setWkid(Number(value))}
                min={1}
            />
            <InputField
                labelClassName="no-flex"
                inputFieldId="cellsize"
                label="Celle størrelse (cm)"
                type="number"
                value={cellSize}
                onChange={(value) => setCellSize(Number(value))}
                min={1}
            />
            <span className="flex-row space-between">
                <label className="input-field-label">Desimaltegn</label>
                <RadioButtonGroup
                    onChange={(value) => setDecimalSeparator(value === 0 ? "Punkt" : "Komma")}
                    options={["Punkt", "Komma"]}
                    selectedIndex={decimalSeparator === "Punkt" ? 0 : 1}
                />
            </span>
            <NfsButton disabled={selectedFiles.length === 0 || isUploading} className="no-flex" onClick={importFiles}>
                Importer og legg til i dybdedatatjeneste
            </NfsButton>

            <span>
                <strong>Valgte filer:</strong>
            </span>
            {selectedFiles.map((file) => (
                <div className="flex-row space-between small-gap w-100" key={file.name}>
                    <span className="break-all">{file.name}</span>
                    <button className="nfs-button no-flex outline warning" onClick={() => removeFile(file.name)}>
                        <TrashIcon />
                    </button>
                </div>
            ))}
        </div>
    );
};

export default DepthDataImport;
